import { defineMessages } from "react-intl";
import { Store } from "redux";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import { TourStepAction } from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import i18nService from "@app/config/I18nService";
import { getApiService } from "@app/store/api";
import { getOrganizationId } from "@app/store/iam";
import type { Actions, RootState } from "@app/store/rootReducer";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

const errorMessages = defineMessages({
  emailDisabled: {
    id: "guides.sendingEmails.error.emailDisabled",
    defaultMessage:
      "Unfortunately this record has no email, please start the tour over and choose a record that has an email.",
  },
  noEmailMenu: {
    id: "guides.sendingEmails.error.noEmailMenu",
    defaultMessage:
      "Cannot find the Email action in this menu. Please start the tour over and try choosing another record.",
  },
  noRecords: {
    id: "guides.sendingEmails.error.noRecords",
    defaultMessage:
      "This tour requires companies, but you don't have any. Feel free to create one and then re-start this tour again.",
  },
  noSuitableRecords: {
    id: "guides.sendingEmails.error.noSuitableRecords",
    defaultMessage:
      "This tour requires companies which have an email, but looks like you don’t have a record like this. Feel free to create a new one or update an existing and restart this tour.",
  },
});

export const checkForCompaniesWithEmail = async (
  store: Store<RootState, Actions>
): Promise<TourStepAction | void> => {
  const filterModel = createAndCondition([
    createSimpleCondition(CommonFieldName.EMAIL, FilterOperator.NOT_EMPTY),
  ]);

  const apiService = getApiService(store.getState());
  const orgId = getOrganizationId(store.getState());
  const response = await apiService!.fetchCompanies(orgId!, {
    $filters: convertToPlatformFilterModel(filterModel, [], companyFieldModel),
    $limit: 0,
  });

  if (response.accessible === 0) {
    // show "no records" message
    return { action: "error", message: i18nService.formatMessage(errorMessages.noRecords) };
  }

  if (response.total === 0) {
    // show "no records with email" message
    return {
      action: "error",
      message: i18nService.formatMessage(errorMessages.noSuitableRecords),
    };
  }
};

export const checkEmailMenuItem = async (): Promise<TourStepAction | void> => {
  const selector = ".ant-dropdown-menu-submenu[data-item-id=email]";
  await new Promise<void>((resolve) =>
    waitForElement(selector, resolve, { failureCallback: resolve })
  );

  const element = document.querySelector(selector);
  if (!element) {
    return {
      action: "error",
      message: i18nService.formatMessage(errorMessages.noEmailMenu),
    };
  }

  if (element.className.includes("disabled")) {
    return {
      action: "error",
      message: i18nService.formatMessage(errorMessages.emailDisabled),
    };
  }
};

export const closeComposeModal = async () => {
  const closeIcon = document.querySelector("[class*=EmailComposeModal_modal] .ant-modal-close svg");
  if (!closeIcon) {
    return;
  }

  const event = new Event("click", { bubbles: true, cancelable: true });
  closeIcon.dispatchEvent(event);

  const yesButtonSelector = "[class*=EmailComposeModal_confirmation] .ant-btn-primary";
  await new Promise<void>((resolve) =>
    waitForElement(yesButtonSelector, resolve, { failureCallback: resolve, timeout: 1000 })
  );
  const yesButton = document.querySelector(yesButtonSelector);
  if (yesButton) {
    (yesButton as HTMLElement).click();
  }
};
